// Browser entry — canon Phase 4. Wires browser adapters into the Analytics core and
// exposes `initAnalytics`. The esbuild IIFE bundle attaches this as window.FixGardenAnalytics.
import { Analytics, createAnalytics } from './analytics';
import { Identity } from './ids';
import { EventQueue } from './queue';
import { createPersistentStore, createSessionStore } from './storage';
import { createIndexedDbStore } from './indexedDbStore';
import { createTransport } from './transport';
import { captureYandex } from './yandex';
import { deviceType, referrer } from './browserContext';
import { createVisibilityTracker, type VisibilityTracker } from './visibility';
import { SDK_DEFAULTS, type AnalyticsConfig } from './types';

export type FixGardenAnalytics = Analytics & { visibility: VisibilityTracker };

function fallbackUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function initAnalytics(config: AnalyticsConfig): FixGardenAnalytics {
  const ids = {
    uuid: (): string =>
      typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : fallbackUuid(),
  };
  const clock = { now: (): number => Date.now() };
  const logger = {
    debug: (...args: unknown[]): void => {
      if (config.debug) console.debug(...args);
    },
    warn: (...args: unknown[]): void => console.warn(...args),
  };
  const scheduler = {
    schedule: (fn: () => void, ms: number): (() => void) => {
      const handle = setTimeout(fn, ms);
      return () => clearTimeout(handle);
    },
  };

  const transport = createTransport(config.endpoint);
  const identity = new Identity(createPersistentStore(), createSessionStore(), ids);
  const queue = new EventQueue(createIndexedDbStore(), transport, clock, scheduler, logger, {
    batchSize: config.batchSize ?? SDK_DEFAULTS.batchSize,
    flushIntervalMs: config.flushIntervalMs ?? SDK_DEFAULTS.flushIntervalMs,
    maxRetries: config.maxRetries ?? SDK_DEFAULTS.maxRetries,
    backoffBaseMs: config.backoffBaseMs ?? SDK_DEFAULTS.backoffBaseMs,
  });

  const sessionContext = (): Record<string, unknown> => {
    const yandex = captureYandex();
    const ctx: Record<string, unknown> = { device: deviceType(), ...yandex.utm };
    if (yandex.yclid) ctx.yclid = yandex.yclid;
    if (yandex.metrica_client_id) ctx.metrica_client_id = yandex.metrica_client_id;
    const ref = referrer();
    if (ref) ctx.referrer = ref;
    return ctx;
  };

  const analytics = createAnalytics({ config, identity, queue, api: transport, clock, logger, sessionContext });
  const tracker = createVisibilityTracker();
  const api: FixGardenAnalytics = Object.assign(analytics, { visibility: tracker });

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void api.flush({ beacon: true });
    });
    window.addEventListener('pagehide', () => void api.flush({ beacon: true }));
  }

  void api.start();
  return api;
}
